import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckBoxOutlineBlankRounded from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRounded from '@mui/icons-material/CheckBoxRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import type { ModulePermission } from 'src/types/modulePermission';
import { useModuleItems } from 'src/components/app/context/ModuleItemsContext';
import Modal from 'src/components/common/Modal';

import { styles } from './index.styles';

export interface CopyItemSelectorProps {
  headline: string;
  accessibleModules: ModulePermission[];
  moduleName: string;
  handleCopy: () => void;
}

export const CopyItemSelectorContent: FC<CopyItemSelectorProps> = ({
  headline,
  accessibleModules = [],
  moduleName,
  handleCopy,
}) => {
  const { t } = useTranslation('admin');
  const [checkedValues, setCheckedValues] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { setCopyModules, setPermissionChecked } = useModuleItems();

  const toggleModal = () => {
    setIsOpen((prevState) => !prevState);
  };

  const handleSelect = (checkedName: string) => {
    const newNames = checkedValues?.includes(checkedName)
      ? checkedValues?.filter((name) => name !== checkedName)
      : [...(checkedValues ?? []), checkedName];

    setCheckedValues(newNames);

    return newNames;
  };

  const handleSubmit = () => {
    setCopyModules(checkedValues);
    setPermissionChecked(true);
    handleCopy();
    toggleModal();
  };

  return (
    <>
      <IconButton
        onClick={() => {
          setConfirm(false);
          setIsOpen(true);
        }}
        color="primary"
        // if accessibleModules is less than 2 it means there is no other module to copy to
        disabled={accessibleModules.length < 2}
      >
        <ContentCopyRoundedIcon />
      </IconButton>
      <Modal isOpen={isOpen} onClose={toggleModal} headline={headline}>
        {!confirm ? (
          <>
            <Typography variant="h6">
              {t('admin:Detail.CopyModule.copyModuleText')}
            </Typography>
            <Stack
              sx={{
                pt: 0.75,
              }}
            >
              {accessibleModules.map((item, index) => {
                if (item.module.name !== moduleName) {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<CheckBoxOutlineBlankRounded />}
                          checkedIcon={<CheckBoxRounded />}
                          checked={checkedValues.includes(item.module.name)}
                          onChange={() => handleSelect(item.module.name)}
                          sx={styles.checkbox}
                        />
                      }
                      key={index}
                      label={item.module.name}
                      sx={styles.formLabel}
                    />
                  );
                }

                return null;
              })}
            </Stack>

            <DialogActions>
              <Button onClick={() => setConfirm(true)} variant="cta" fullWidth>
                {t('admin:Detail.CopyModule.accept')}
              </Button>

              <Button
                onClick={toggleModal}
                variant="cta"
                color="secondary"
                fullWidth
              >
                {t('admin:Detail.CopyModule.overwriteCancel')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Typography variant="h6">
              {t('admin:Detail.CopyModule.overwriteQuestion')}
            </Typography>
            <DialogActions>
              <Button
                data-test-id="user-admin-detail-confirm-copy"
                onClick={handleSubmit}
                variant="cta"
                fullWidth
              >
                {t('admin:Detail.CopyModule.accept')}
              </Button>

              <Button
                onClick={toggleModal}
                variant="cta"
                color="secondary"
                fullWidth
              >
                {t('admin:Detail.CopyModule.overwriteCancel')}
              </Button>
            </DialogActions>
          </>
        )}
      </Modal>
    </>
  );
};
